import React, { useState } from 'react'
import { ActionChip, Row } from '@fs/zion-ui'
import { setPreference, usePreference } from '@fs/zion-preferences'
import { setLabsPreference } from '@fs/zion-soft-launch'
import { useTranslation } from 'react-i18next'
import { localStorageRemoveItem } from '../../../utils/localStorage'
import { SOURCE_CENTRIC_COMMUNITY_URL } from '../../constants'
import useSourceCentricLabIsOn, { SOURCE_CENTRIC_LABS_ID } from './useSourceCentricLabIsOn'

const SOFT_LAUNCH_PREF = 'softLaunch.treeWeb_personR9_sourceCentricDetails'

function FeedbackWelcomeChip(): JSX.Element {
  const [t] = useTranslation()

  return (
    <ActionChip
      inline
      color="blue"
      to={SOURCE_CENTRIC_COMMUNITY_URL}
      external
      target="_blank"
      linkName="Source Centric Feedback"
      data-testid="banner-feedback-welcome"
      label={t('tree-person.feedback-welcome.label')}
    />
  )
}

export default function BannerExperimentChips(): JSX.Element {
  const [t] = useTranslation()
  const [optingOut, setOptingOut] = useState(false)
  const labIsOn = useSourceCentricLabIsOn()
  const softLaunchPref = usePreference<boolean>(SOFT_LAUNCH_PREF)

  // Opt-out clears the experiment however the user got into it: when the lab is on we remove it from
  // the lab, and we always overwrite the soft-launch pref. A soft-launch opt-in is stored as `true`,
  // so we overwrite it with `false` to record an explicit opt-out; otherwise the user reached the
  // experience via the lab, so we clear the pref back to an empty string. On success we drop the
  // cached flag (so the reload doesn't briefly flash the source-centric UI) and reload so
  // useSourceCentricDetailsFlag re-resolves the classic details page on the same PID. On failure we
  // re-enable the chip rather than reload into an unchanged page.
  const handleOptOut = async (): Promise<void> => {
    setOptingOut(true)
    try {
      if (labIsOn) {
        await setLabsPreference({ labsId: SOURCE_CENTRIC_LABS_ID, isOptIn: false })
      }
      const nextSoftLaunchPref = softLaunchPref === true ? false : ''
      await setPreference(SOFT_LAUNCH_PREF, nextSoftLaunchPref)
      localStorageRemoveItem('tree-person.sourceCentricDetails')
      window.location.reload()
    } catch {
      setOptingOut(false)
    }
  }

  return (
    <Row wrap gutters="nano" guttersY="xs">
      <FeedbackWelcomeChip />
      <ActionChip
        inline
        disabled={optingOut}
        onClick={handleOptOut}
        data-testid="banner-opt-out-experiment"
        label={t('tree-person.opt-out-experiment.label')}
        linkName="Source Centric Opt Out"
      />
    </Row>
  )
}

// The feedback link stays live while loading; the opt-out action is disabled until the page is ready.
export function BannerExperimentChipsSkeleton(): JSX.Element {
  const [t] = useTranslation()

  return (
    <Row wrap gutters="nano" guttersY="xs">
      <FeedbackWelcomeChip />
      <ActionChip
        inline
        disabled
        data-testid="banner-opt-out-experiment-skeleton"
        label={t('tree-person.opt-out-experiment.label')}
      />
    </Row>
  )
}
